/**
 * ReikiConsent Component
 *
 * Comprehensive Reiki consent form with multiple sections:
 * Consent and Waiver, Testimonial Disclaimer, Assumption of Risk,
 * Governing Law, and Exclusive Jurisdiction
 */

import React from "react";
import {
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  CardContent,
} from "@material-ui/core";
import SignatureCanvas from "react-signature-canvas";

function ReikiConsent({
  checked,
  onChange,
  answers,
  signatureRef,
  onClearSignature,
  readOnly,
  version,
}) {
  if (!checked) {
    return null;
  }

  return (
    <div>
      {readOnly && version && (
        <Card style={{ marginBottom: 16, backgroundColor: "#fffacd" }}>
          <CardContent>
            <Typography variant="body2" style={{ color: "#856404" }}>
              <b>Historical View:</b> This form was submitted on{" "}
              {version.submittedAt?.split("T")[0]} under consent version{" "}
              {version.versionId} and is displayed for reference only.
            </Typography>
          </CardContent>
        </Card>
      )}
      <Typography variant="h4" style={{ textAlign: "left" }}>
        Reiki Consent Form
      </Typography>
      <br />
      <br />
      <Typography variant="h5" style={{ textAlign: "left" }}>
        Consent and Waiver
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        <b>What to Expect</b> <br />
        Reiki is a gentle, non-invasive energy modality that can help to restore
        the flow of energy throughout the body resulting in a general sense of
        calm, warmth, rejuvenation and restoration of overall wellness. In
        person sessions take place fully clothed, lying on a massage table
        facing upward. Sitting is an option if it is more comfortable.
        <br /> <br />
        The treatment options for Reiki include a light hands-on contact, hands
        a slight distance away from the body or through long distance also known
        as remote sessions. Long distance or remote sessions occur without
        sharing a physical space together, however Reiki is sent to you. With
        Reiki, there is a trust that the energy will flow where it is needed in
        your system for your highest good.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.reikiConsentAndWaiver || false}
            onChange={(e) =>
              onChange("reikiConsentAndWaiver", e.target.checked)
            }
            color="primary"
            name="reikiConsentAndWaiver"
            disabled={readOnly}
          />
        }
        label="I understand"
      />
      <br /> <br />
      <Typography variant="h5" style={{ textAlign: "left" }}>
        Testimonial Disclaimer
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Any testimonials, examples, stories or information provided to you do
        not constitute a warranty, guarantee or prediction regarding the outcome
        of your session for any particular issue or purpose. The testimonials
        are given verbatim except for correction of grammar or typing errors or
        shortened where parts of the testimonial were considered irrelevant to
        the practice. People agreeing to the use of their testimonial have not
        received any material compensation.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.reikiTestimonialDisclaimer || false}
            onChange={(e) =>
              onChange("reikiTestimonialDisclaimer", e.target.checked)
            }
            color="primary"
            name="reikiTestimonialDisclaimer"
            disabled={readOnly}
          />
        }
        label="I agree"
      />
      <br /> <br />
      <Typography variant="h5" style={{ textAlign: "left" }}>
        Assumption of Risk and Release of Claims
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Any information you, the client, shares with me during our session(s) is
        confidential. I may, however, discuss situations with my professional
        mentor(s) for the purpose of continuing my professional development.
        These discussions will provide, generic situation, rather than names,
        and will not include any other identifying information. <br /> <br /> If
        you have a physical injury, disease condition, or mental health
        disorder, consult a licensed health or mental health professional. I do
        not and cannot medically diagnose or prescribe medical treatment(s). I
        will not advise you to discontinue any current medical treatment. While
        Loretta Green can offer general wellness information, body awareness
        practices and information related to Reiki, the Reiki practitioner is
        not a regulated mental health or medical professional in her or his home
        province of Ontario, Canada. You are encouraged to seek professional
        advice as appropriate for your mental and physical health. <br /> <br />
        At any time you wish to, you can stop the session(s). <br /> <br /> In
        signing this disclaimer and waiver, you agree that the Reiki
        practitioner may work with you. While Reiki can provide healing
        benefits, results vary for each individual. It is recommended that they
        are applied in conjunction with treatment prescribed by your medical
        doctor and/or other qualified health care professionals. <br /> <br />
        Although there are no known harmful effects from Reiki, I do not hold
        the Reiki practitioner and Appleby Wellness Centre responsible for any
        possible effects that may cause any discomfort and agree to take full
        responsibility for my self-care. <br /> <br /> By continuing, you agree
        to fully release, indemnify, and hold harmless, Appleby Wellness Centre
        and their heirs, personal representatives, consultants, employees and
        assigns from any claim or liability whatsoever and for any damage or
        injury, personal, financial, emotional psychological or otherwise, which
        you may incur arising at any time out of or in relation to your
        session(s). lf any court of law rules that any part of the Disclaimer in
        invalid, the Disclaimer stands as if those parts were struck out. <br />{" "}
        <br /> By working with the Reiki practitioner, you agree to all of the
        above. By completing the below, you acknowledge you are in full control
        of your intellectual abilities.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.reikiAssumptionOfRisk || false}
            onChange={(e) =>
              onChange("reikiAssumptionOfRisk", e.target.checked)
            }
            color="primary"
            name="reikiAssumptionOfRisk"
            disabled={readOnly}
          />
        }
        label="I agree"
      />
      <br /> <br />
      <Typography variant="h5" style={{ textAlign: "left" }}>
        Governing Law
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        The Parties hereby agree that: <br /> <br />
        a) All aspects of the relationship between you, the client, and the
        Reiki practitioner, and <br /> <br /> b) The resolution of any and all
        disputes arising from or in connection with that relationship, including
        any disputes arising under or in connection with this Agreement, shall
        be governed by and construed in accordance with the laws of the Province
        of Ontario (other than conflict of law; rules) and the laws of Canada
        applicable therein.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.reikiGoverningLaw || false}
            onChange={(e) => onChange("reikiGoverningLaw", e.target.checked)}
            color="primary"
            name="reikiGoverningLaw"
            disabled={readOnly}
          />
        }
        label="I agree"
      />
      <br /> <br />
      <Typography variant="h5" style={{ textAlign: "left" }}>
        Exclusive Jurisdiction
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        The Parties hereby acknowledge that the session(s) received by you, the
        client, from Appleby Wellness Centre provided in/from the Province of
        Ontario and that the Courts of Ontario shall have exclusive jurisdiction
        to hear any complaint, demand, claim, proceeding or cause of action,
        whatsoever arising from or in connection with the session(s), or from
        any other aspect of the relationship between you, the client, and
        Appleby Wellness Centre.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.reikiExclusiveJurisdiction || false}
            onChange={(e) =>
              onChange("reikiExclusiveJurisdiction", e.target.checked)
            }
            color="primary"
            name="reikiExclusiveJurisdiction"
            disabled={readOnly}
          />
        }
        label="I agree"
      />
      <br /> <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please sign in the box below.
      </Typography>
      <div
        style={{
          borderStyle: "dotted",
          borderWidth: 2,
          borderColor: "#D3D3D3",
          width: 530,
          height: 240,
          borderRadius: 4,
        }}
      >
        <SignatureCanvas
          penColor="black"
          ref={signatureRef}
          canvasProps={{
            width: 530,
            height: 240,
            className: "consentReikiSig",
          }}
          disabled={readOnly}
        />
      </div>
      <br />
      {!readOnly && <Button onClick={onClearSignature}>Clear</Button>}
      <br />
    </div>
  );
}

export default ReikiConsent;
